/* eslint-disable */
import { useLayoutEffect, useEffect, useCallback } from 'react';

// ============================================================
// useFitBar — the filter bar fits itself by MEASUREMENT, not by breakpoints.
// After every render it clears the collapse classes, measures what the bar
// needs against what it has, and applies the steps below — least important
// first — until it fits. The steps are the prototype's, in the same order.
// Classes are applied imperatively on the bar node and the field wrappers
// (React re-sets className only when its own prop changes, and this effect
// runs after every commit, so the result always matches the latest render).
// ============================================================

const STEP_CLASSES = ['cK', 'cSt2', 'cCnt', 'cYl', 'cYc', 'cYs', 'cLn', 'cCtl', 'cAlt', 'cSt1', 'cPs', 'cBtn', 'cSpec', 'cTog', 'cDens', 'cPs2', 'cPrim', 'cAux', 'cViews', 'cGroup', 'cSt0', 'cMore', 'cName', 'cSort', 'cBtnx', 'hasMore'];

function visible(el) { return getComputedStyle(el).display !== 'none'; }

function barNeed(bar, strip) {
  const kids = Array.from(bar.children).filter(visible);
  const cs = getComputedStyle(bar);
  const gap = parseFloat(cs.gap) || 8;
  const pad = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight);
  let sum = pad + Math.max(0, kids.length - 1) * gap;
  for (const k of kids) sum += (k === strip) ? 0 : k.getBoundingClientRect().width;
  if (strip) {
    const fk = Array.from(strip.children).filter(visible);
    for (const k of fk) sum += k.getBoundingClientRect().width;
    sum += Math.max(0, fk.length - 1) * 6;
  }
  return sum;
}

export function fitBar(bar, s) {
  if (!bar) return;
  const strip = bar.querySelector('[data-ffs]');
  STEP_CLASSES.forEach((c) => s[c] && bar.classList.remove(s[c]));
  bar.querySelectorAll('[data-ffw]').forEach((e) => e.classList.remove(s.collapsed));
  const fits = () => barNeed(bar, strip) <= bar.clientWidth + 0.5;
  if (fits()) return;

  const add = (c) => s[c] && bar.classList.add(s[c]);
  const collapse = (e) => { e.classList.add(s.collapsed); add('hasMore'); };
  const ffw = strip ? Array.from(strip.querySelectorAll(':scope > [data-ffw]')) : [];
  const lists = ffw.filter((e) => e.dataset.ffw === 'list').sort((a, b) => (+b.dataset.prio || 0) - (+a.dataset.prio || 0));
  const inline = ffw.filter((e) => ['alt', 'seg', 'chips'].includes(e.dataset.ffw) && e.dataset.hasAlt !== '1').reverse();
  const specs = ffw.filter((e) => e.dataset.ffw === 'spec');
  const hasTog = ffw.some((e) => e.dataset.ffw === 'tog');

  const steps = [
    () => add('cK'), () => add('cSt2'), () => add('cCnt'),
    ...lists.slice(0, 2).map((e) => () => collapse(e)),
    () => add('cYl'), () => add('cYc'), () => add('cYs'), () => add('cLn'),
    () => add('cCtl'), () => add('cAlt'),
    ...lists.slice(2).map((e) => () => collapse(e)),
    () => add('cSt1'), () => add('cPs'), () => add('cBtn'), () => add('cSpec'),
    () => { add('cTog'); if (hasTog) add('hasMore'); },
    ...inline.map((e) => () => collapse(e)),
    () => add('cDens'), () => add('cPs2'),
    ...specs.map((e) => () => collapse(e)),
    () => add('cPrim'), () => add('cAux'), () => add('cViews'), () => add('cGroup'), () => add('cSt0'), () => add('cMore'), () => add('cName'), () => add('cSort'), () => add('cBtnx'),
  ];
  for (const st of steps) { st(); if (fits()) break; }
}

export function useFitBar(barRef, s) {
  const run = useCallback(() => fitBar(barRef.current, s), [barRef, s]);
  // After every commit — the bar's own render is what changes what it needs.
  useLayoutEffect(() => { run(); });
  useEffect(() => {
    const onResize = () => run();
    window.addEventListener('resize', onResize);
    window.addEventListener('load', onResize);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(onResize).catch(() => {});
    // fonts.ready resolves when nothing is loading NOW — a face first used by a
    // later render loads after it, and the bar measured in the fallback face
    // would keep that fit; loadingdone fires on every such late load (S7 fix).
    const onFonts = () => onResize();
    if (document.fonts && document.fonts.addEventListener) document.fonts.addEventListener('loadingdone', onFonts);
    const bar = barRef.current;
    const onEnd = (e) => { if (e.propertyName === 'width') run(); };
    bar && bar.addEventListener('transitionend', onEnd);
    const ro = typeof ResizeObserver !== 'undefined' && bar ? new ResizeObserver(() => run()) : null;
    ro && ro.observe(bar);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
      if (document.fonts && document.fonts.removeEventListener) document.fonts.removeEventListener('loadingdone', onFonts);
      bar && bar.removeEventListener('transitionend', onEnd);
      ro && ro.disconnect();
    };
  }, [run, barRef]);
  return run;
}
